import type { ContradictionFieldRef } from '../../../services/api/types.gen';
import {
  ACTIVE_DIAGNOSIS_CATEGORY,
  MEDICATIONS_CATEGORY,
  MEDICATIONS_SECTION_ID,
  SKIN_SECTION_ID,
  WOUNDS_CATEGORY,
} from '../OASISFormLabels';
import {
  escapeCssAttributeValue,
  isVisibleOasisNavigationTarget,
} from '../oasisNavigationVisibility';

/**
 * Props marking a DOM node as a contradiction jump target. Spread onto the
 * module wrapper (no `rowId`) or onto an individual row (`rowId` = the row's
 * database id, matching `matched_ids` from the engine).
 *
 * Every anchor goes through here so the attribute names live in one place and
 * `source` is checked against the generated backend union — a renamed or new
 * source then fails the build instead of silently making "jump to problem"
 * do nothing.
 */
export function contradictionAnchor(
  source: ContradictionFieldRef['source'],
  rowId?: string
): Record<string, string> {
  return rowId
    ? {
        'data-contradiction-source': source,
        'data-contradiction-field': rowId,
      }
    : { 'data-contradiction-source': source };
}

/**
 * Marks a module wrapper as still fetching its rows. Wound and medication
 * modules mount their wrapper before the rows exist, so without this the
 * whole-module fallback resolves instantly and a cross-section jump lands on
 * the module instead of the row the engine matched.
 */
export function contradictionLoadingAnchor(
  isLoading: boolean
): Record<string, string> {
  return isLoading ? { 'data-contradiction-loading': 'true' } : {};
}

/**
 * Non-qid contradiction anchors (active diagnoses / diagnosis list / meds /
 * wounds), most specific first:
 *
 * 1. ``matched_ids`` — the rows the engine says actually matched this rule.
 *    Resolved per evaluation, so one location can name several rows (two
 *    active venous ulcers), and every one of them is returned.
 * 2. ``field`` — static rule metadata naming a fixed sub-field (e.g. the
 *    M1028 ``diabetes_mellitus`` checkbox).
 * 3. The whole-module container, for findings about a count or about a row
 *    that does not exist. Suppressed while the module is still loading its
 *    rows (see `contradictionLoadingAnchor`) and the location names rows —
 *    otherwise the wrapper wins the race against its own rows and a jump
 *    into a not-yet-loaded section degrades to module-level highlighting.
 *
 * Returns every visible match at the first tier that resolves any, so callers
 * can highlight them all. Empty when nothing on the page matches.
 */
export function findContradictionSourceTargets(
  location: ContradictionFieldRef
): HTMLElement[] {
  const source = escapeCssAttributeValue(location.source);
  const matchedIds = location.matched_ids || [];
  const field = location.field ? escapeCssAttributeValue(location.field) : null;

  const selectorTiers: string[][] = [];
  if (matchedIds.length > 0) {
    selectorTiers.push(
      matchedIds.map(
        id =>
          `[data-contradiction-source="${source}"][data-contradiction-field="${escapeCssAttributeValue(id)}"]`
      )
    );
  }
  if (field) {
    selectorTiers.push([
      `[data-contradiction-source="${source}"][data-contradiction-field="${field}"]`,
    ]);
  }
  // `:not([data-contradiction-field])` keeps this tier to the module wrapper.
  // Row anchors repeat the source attribute, so without it a fallback would
  // highlight the module *and* every row inside it.
  const moduleSelector = `[data-contradiction-source="${source}"]:not([data-contradiction-field])`;
  // A row tier that found nothing may just be early: the wrapper mounts before
  // the fetched rows. Let the caller retry rather than settling for the module.
  const rowTiersExist = selectorTiers.length > 0;
  if (
    !rowTiersExist ||
    !document.querySelector(
      `${moduleSelector}[data-contradiction-loading="true"]`
    )
  ) {
    selectorTiers.push([moduleSelector]);
  }

  for (const tier of selectorTiers) {
    const found: HTMLElement[] = [];
    for (const selector of tier) {
      for (const node of document.querySelectorAll<HTMLElement>(selector)) {
        if (isVisibleOasisNavigationTarget(node)) found.push(node);
      }
    }
    if (found.length > 0) return found;
  }
  return [];
}

/**
 * Adds the page hosting each wound and medication module to a qid → section
 * map, keyed by its contradiction source. A form whose schema has no Skin or
 * Medications section shows that module on its own page instead.
 */
export function withModuleCategories(
  qidToCategoryMap: Record<string, string>,
  categories: readonly string[]
): Record<string, string> {
  return {
    ...qidToCategoryMap,
    wound_list: categories.includes(WOUNDS_CATEGORY)
      ? WOUNDS_CATEGORY
      : SKIN_SECTION_ID,
    medication_list: categories.includes(MEDICATIONS_CATEGORY)
      ? MEDICATIONS_CATEGORY
      : MEDICATIONS_SECTION_ID,
  };
}

/**
 * OASIS section that hosts a non-qid contradiction source module, if any.
 * Used for scrubber chips and pending section switches before the DOM anchor mounts.
 */
export function sectionIdForContradictionSource(
  source: ContradictionFieldRef['source'],
  qidToCategoryMap: Record<string, string> = {}
): string | null {
  switch (source) {
    case 'active_diagnoses':
    case 'diagnosis_list':
      return ACTIVE_DIAGNOSIS_CATEGORY;
    case 'wound_list':
      return qidToCategoryMap.wound_list ?? SKIN_SECTION_ID;
    case 'medication_list':
      return qidToCategoryMap.medication_list ?? MEDICATIONS_SECTION_ID;
    case 'oasis_item':
    case 'visit_question':
      // Located by qid, not by an embedded module.
      return null;
  }
}

/** Sections a single error_location belongs to (for scrubber filter + jump). */
export function categoriesForContradictionLocation(
  location: ContradictionFieldRef,
  qidToCategoryMap: Record<string, string>
): string[] {
  const embeddedSection = sectionIdForContradictionSource(
    location.source,
    qidToCategoryMap
  );
  if (embeddedSection) {
    return [embeddedSection];
  }
  return Array.from(
    new Set(
      (location.qids || [])
        .map(qid => qidToCategoryMap[qid])
        .filter((category): category is string => Boolean(category))
    )
  );
}

export function contradictionLocationIsOnCategory(
  location: ContradictionFieldRef,
  category: string | null,
  qidToCategoryMap: Record<string, string>
): boolean {
  if (!category) return false;
  return categoriesForContradictionLocation(
    location,
    qidToCategoryMap
  ).includes(category);
}

/** Primary section id for a location (first related category), if any. */
export function primaryCategoryForContradictionLocation(
  location: ContradictionFieldRef,
  qidToCategoryMap: Record<string, string>
): string | null {
  return (
    categoriesForContradictionLocation(location, qidToCategoryMap)[0] ?? null
  );
}
